import React, { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	IconButton,
	Box,
	Typography,
	Divider,
	FormControlLabel,
	Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { AddonGroupInterface, AddonInterface } from '../types';

interface AddonGroupEditModalProps {
	isVisible: boolean;
	onClose: () => void;
	addonGroup: AddonGroupInterface | null;
	onSave: (addonGroup: AddonGroupInterface) => void;
	onDelete?: (addonGroupId: number) => void;
	currency: string | undefined;
	isNew?: boolean;
}

const AddonGroupEditModal: React.FC<AddonGroupEditModalProps> = ({
	isVisible,
	onClose,
	addonGroup,
	onSave,
	onDelete,
	currency,
	isNew = false,
}) => {
	const [name, setName] = useState('');
	const [minAddonsSelect, setMinAddonsSelect] = useState(0);
	const [maxAddonsSelect, setMaxAddonsSelect] = useState(1);
	const [isRequired, setIsRequired] = useState(false);
	const [addons, setAddons] = useState<AddonInterface[]>([]);

	useEffect(() => {
		if (addonGroup) {
			setName(addonGroup.name || '');
			setMinAddonsSelect(addonGroup.min_addons_select || 0);
			setMaxAddonsSelect(addonGroup.max_addons_select || 1);
			setIsRequired(addonGroup.is_required || false);
			setAddons(addonGroup.addons || []);
		} else {
			// Reset for new group
			setName('New Addon Group');
			setMinAddonsSelect(0);
			setMaxAddonsSelect(1);
			setIsRequired(false);
			setAddons([]);
		}
	}, [addonGroup]);

	const handleSave = () => {
		const updatedGroup: AddonGroupInterface = {
			id: addonGroup?.id || Date.now(),
			name,
			min_addons_select: minAddonsSelect,
			max_addons_select: maxAddonsSelect,
			is_required: isRequired,
			addons,
		};
		onSave(updatedGroup);
		onClose();
	};

	const handleDelete = () => {
		if (addonGroup?.id && onDelete) {
			onDelete(addonGroup.id);
			onClose();
		}
	};

	const addAddon = () => {
		const newAddon: AddonInterface = {
			id: Date.now(),
			name: 'New Addon',
			price: 0,
			is_multi: false,
			quantity: 1,
			is_out_of_stock: false,
		};
		setAddons([...addons, newAddon]);
	};

	const updateAddon = (index: number, field: keyof AddonInterface, value: any) => {
		const updated = [...addons];
		updated[index] = { ...updated[index], [field]: value };
		setAddons(updated);
	};

	const deleteAddon = (index: number) => {
		setAddons(addons.filter((_, i) => i !== index));
	};

	return (
		<Dialog
			open={isVisible}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: { minHeight: '50vh' },
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					pb: 1,
				}}
			>
				<span>{isNew ? 'Add Addon Group' : 'Edit Addon Group'}</span>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ pt: 2 }}>
				{/* Group Settings */}
				<Box sx={{ mb: 3 }}>
					<TextField
						fullWidth
						label="Group Name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						sx={{ mb: 2 }}
					/>

					<Box display="flex" gap={2} mb={2}>
						<TextField
							label="Min Select"
							type="number"
							value={minAddonsSelect}
							onChange={(e) => setMinAddonsSelect(parseInt(e.target.value) || 0)}
							sx={{ width: 150 }}
							inputProps={{ min: 0 }}
						/>
						<TextField
							label="Max Select"
							type="number"
							value={maxAddonsSelect}
							onChange={(e) => setMaxAddonsSelect(parseInt(e.target.value) || 1)}
							sx={{ width: 150 }}
							inputProps={{ min: 1 }}
						/>
						<FormControlLabel
							control={
								<Checkbox checked={isRequired} onChange={(e) => setIsRequired(e.target.checked)} />
							}
							label="Required Group"
						/>
					</Box>
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Addons Section */}
				<Box>
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
						<Typography variant="h6">Addons in this Group</Typography>
						<Button startIcon={<AddIcon />} onClick={addAddon} variant="outlined" size="small">
							Add Addon
						</Button>
					</Box>

					{addons.length === 0 ? (
						<Box
							sx={{
								textAlign: 'center',
								py: 4,
								border: '1px dashed #ccc',
								borderRadius: 1,
							}}
						>
							<Typography variant="body2" color="text.secondary">
								No addons yet. Click "Add Addon" to create one.
							</Typography>
						</Box>
					) : (
						<Box sx={{ display: 'grid', gap: 2 }}>
							{addons.map((addon, index) => (
								<Box
									key={addon.id || index}
									sx={{
										p: 2,
										border: '1px solid',
										borderColor: 'divider',
										borderRadius: 1,
										bgcolor: 'grey.50',
									}}
								>
									<Box display="flex" gap={2} mb={2}>
										<TextField
											label="Addon Name"
											value={addon.name}
											onChange={(e) => updateAddon(index, 'name', e.target.value)}
											sx={{ flex: 2 }}
											size="small"
										/>
										<TextField
											label={`Price (${currency || '$'})`}
											type="number"
											value={addon.price || 0}
											onChange={(e) =>
												updateAddon(index, 'price', parseFloat(e.target.value) || 0)
											}
											sx={{ width: 120 }}
											inputProps={{ min: 0, step: 0.01 }}
											size="small"
										/>
										<IconButton color="error" onClick={() => deleteAddon(index)} size="small">
											<DeleteIcon />
										</IconButton>
									</Box>

									<Box display="flex" gap={2}>
										<FormControlLabel
											control={
												<Checkbox
													checked={addon.is_multi || false}
													onChange={(e) => updateAddon(index, 'is_multi', e.target.checked)}
												/>
											}
											label="Allow Multiple"
										/>
										{addon.is_multi && (
											<TextField
												label="Max Quantity"
												type="number"
												value={addon.quantity || 1}
												onChange={(e) =>
													updateAddon(index, 'quantity', parseInt(e.target.value) || 1)
												}
												sx={{ width: 120 }}
												inputProps={{ min: 1 }}
												size="small"
											/>
										)}
										<FormControlLabel
											control={
												<Checkbox
													checked={addon.is_out_of_stock || false}
													onChange={(e) =>
														updateAddon(index, 'is_out_of_stock', e.target.checked)
													}
												/>
											}
											label="Out of Stock"
										/>
									</Box>
								</Box>
							))}
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ p: 2, pt: 1 }}>
				{!isNew && onDelete && (
					<Button onClick={handleDelete} color="error" sx={{ mr: 'auto' }}>
						Delete Group
					</Button>
				)}
				<Button onClick={onClose}>Cancel</Button>
				<Button onClick={handleSave} variant="contained">
					{isNew ? 'Add Group' : 'Save Changes'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddonGroupEditModal;
